/**
 * NAT HOF CRM Tracking Module v3
 * ==============================
 * UNIFIED TRACKING + IDENTITY + SCORING + TAGS
 *
 * Single source of truth for all CRM events sent to DataCrazy.
 * Implements: identity resolution, consent gating, dedupe, intent scoring.
 *
 * PRINCIPLES:
 * - NO page_view/scroll spam
 * - Dedupe by event + lead + time window
 * - Respect consent: no opt-in => ops:no_consent
 * - No sensitive/diagnostic data
 * - Single id:* tag, single intent:* tag at all times
 */

import { supabase } from "@/integrations/supabase/client";

// ============= Storage Keys =============
const ANON_ID_KEY = "nathof_anon_id";
const SESSION_ID_KEY = "nathof_session_id";
const EXTERNAL_ID_KEY = "crm_external_id";
const LEAD_ID_KEY = "nathof_lead_id";
const CONSENT_KEY = "nathof_consent"; // stores only boolean flags, no PII
const SCORE_KEY = "nathof_engagement_score";
const DEDUPE_PREFIX = "nathof_dedupe_";
const FIRST_PAGE_KEY = "nathof_first_page";
const TAGS_APPLIED_KEY = "nathof_tags_applied";

// PII is held ONLY in memory during the session, never persisted to storage
let _inMemoryEmail: string | null = null;
let _inMemoryPhone: string | null = null;

// ============= Dedupe Windows (seconds) =============
const DEDUPE_WINDOWS: Record<string, number> = {
  site_visited: 1800,               // 30 min
  pdp_viewed: 86400,                // 24h per product
  quiz_started: 3600,               // 1h
  quiz_completed: 3600,             // 1h
  add_to_cart: 300,                 // 5 min per product
  begin_checkout: 1800,             // 30 min
  checkout_abandoned: 86400,        // 24h
  purchase: 3600,                   // 1h per order
  routine_step_checked: 86400,      // 1x/day
  skin_log_created: 86400,          // 1x/day
  daily_recommendation_shown: 86400,// 1x/day
  routine_exported_pdf: 300,        // 5 min
  routine_shared: 300,              // 5 min
  routine_link_copied: 300,         // 5 min
  consent_research_opt_in: 3600,    // 1h
};

// ============= Event Governance Allowlist =============
const CRM_ALLOWED_EVENTS = new Set([
  "site_visited",
  "pdp_viewed",
  "quiz_started",
  "quiz_completed",
  "add_to_cart",
  "begin_checkout",
  "checkout_abandoned",
  "purchase",
  "routine_step_checked",
  "skin_log_created",
  "daily_recommendation_shown",
  "routine_exported_pdf",
  "routine_shared",
  "routine_link_copied",
  "consent_research_opt_in",
  "consent_whatsapp_opt_in",
  "consent_whatsapp_opt_out",
  "lead_capture",
  "whatsapp_click",
]);

// Events blocked from CRM (logged for debug only)
const CRM_BLOCKED_EVENTS = new Set([
  "page_view",
  "scroll_depth",
  "scroll",
  "view_pdp", // replaced by pdp_viewed
  "time_on_page",
  "session_start",
  "session_end",
  "click",
]);

// ============= Types =============
export interface ConsentState {
  email_opt_in: boolean;
  whatsapp_opt_in: boolean;
  research_opt_in?: boolean;
  updated_at: string;
}

export interface EngagementScore {
  total: number;
  last_event: string;
  updated_at: string;
}

interface TrackPayload {
  email?: string;
  phone?: string;
  name?: string;
  properties?: Record<string, unknown>;
}

// ============= UUID Generator =============
function createUUID(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch { /* fallback */ }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============= Debug Mode =============
function isCrmDebug(): boolean {
  if (typeof window === "undefined") return false;

  try {
    if (localStorage.getItem("crm_debug") === "1") return true;
    if ((window as any).__CRM_DEBUG__ === true) return true;
    if (new URLSearchParams(window.location.search).get("debug") === "crm") return true;
  } catch { /* ignore */ }

  return false;
}

// ============= Identity Functions =============

/**
 * Get or create anonymous ID (persistent across sessions)
 */
export function getAnonId(): string {
  if (typeof window === "undefined") return createUUID();

  try {
    let anonId = localStorage.getItem(ANON_ID_KEY);
    if (!anonId) {
      anonId = createUUID();
      localStorage.setItem(ANON_ID_KEY, anonId);
    }
    return anonId;
  } catch {
    return createUUID();
  }
}

/**
 * Get or create session ID (per browser tab/session)
 */
export function getSessionId(): string {
  if (typeof window === "undefined") return createUUID();

  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = createUUID();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return createUUID();
  }
}

/**
 * Get external ID (CRM tracking ID)
 */
export function getExternalId(): string {
  if (typeof window === "undefined") return createUUID();

  try {
    let externalId = localStorage.getItem(EXTERNAL_ID_KEY);
    if (!externalId) {
      externalId = createUUID();
      localStorage.setItem(EXTERNAL_ID_KEY, externalId);
    }
    return externalId;
  } catch {
    return createUUID();
  }
}

/**
 * Get lead_id if user has been identified in CRM
 */
export function getLeadId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(LEAD_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Set lead_id after CRM identification
 */
export function setLeadId(leadId: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LEAD_ID_KEY, leadId);
  } catch { /* ignore */ }
}

/**
 * Get stored email (in-memory only, never persisted)
 */
export function getStoredEmail(): string | null {
  // Migrate: clear legacy localStorage PII if present
  if (typeof window !== "undefined") {
    try {
      if (localStorage.getItem("nathof_email")) localStorage.removeItem("nathof_email");
    } catch { /* ignore */ }
  }
  return _inMemoryEmail;
}

/**
 * Get stored phone (in-memory only, never persisted)
 */
export function getStoredPhone(): string | null {
  if (typeof window !== "undefined") {
    try {
      if (localStorage.getItem("nathof_phone")) localStorage.removeItem("nathof_phone");
    } catch { /* ignore */ }
  }
  return _inMemoryPhone;
}

/**
 * Store email for identity resolution (in-memory only)
 */
export function setStoredEmail(email: string): void {
  _inMemoryEmail = email;
  // Clean up legacy storage
  if (typeof window !== "undefined") {
    try { localStorage.removeItem("nathof_email"); } catch { /* ignore */ }
  }
}

/**
 * Store phone for identity resolution (in-memory only)
 */
export function setStoredPhone(phone: string): void {
  _inMemoryPhone = phone;
  // Clean up legacy storage
  if (typeof window !== "undefined") {
    try { localStorage.removeItem("nathof_phone"); } catch { /* ignore */ }
  }
}

/**
 * Get current user_id from Supabase auth
 */
export async function getUserId(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Resolve identity and return the appropriate id:* tag
 * Ensures only ONE id:* tag exists
 */
export function resolveIdentityTag(): string {
  const hasEmail = Boolean(getStoredEmail());
  const hasPhone = Boolean(getStoredPhone());

  if (hasEmail && hasPhone) return "id:email_and_phone";
  if (hasEmail) return "id:email_only";
  if (hasPhone) return "id:phone_only";
  return "id:anonymous";
}

/**
 * Get all id:* tags that should be REMOVED (keeping only current)
 */
function getIdentityTagsToRemove(currentTag: string): string[] {
  const allIdentityTags = ["id:anonymous", "id:email_only", "id:phone_only", "id:email_and_phone"];
  return allIdentityTags.filter(t => t !== currentTag);
}

// ============= Consent Functions =============

/**
 * Get current consent state
 */
export function getConsentState(): ConsentState | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ConsentState;
      // Ensure we only store boolean flags, no PII
      return {
        email_opt_in: !!parsed.email_opt_in,
        whatsapp_opt_in: !!parsed.whatsapp_opt_in,
        research_opt_in: !!parsed.research_opt_in,
        updated_at: parsed.updated_at || "",
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Set consent state (only boolean flags, no PII)
 */
export function setConsentState(consent: Partial<ConsentState>): void {
  if (typeof window === "undefined") return;

  try {
    const current = getConsentState() || { email_opt_in: false, whatsapp_opt_in: false, updated_at: "" };
    const updated: ConsentState = {
      email_opt_in: consent.email_opt_in ?? current.email_opt_in,
      whatsapp_opt_in: consent.whatsapp_opt_in ?? current.whatsapp_opt_in,
      research_opt_in: consent.research_opt_in ?? current.research_opt_in,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

/**
 * Check if user has ANY consent (email or whatsapp)
 */
export function hasAnyConsent(): boolean {
  const state = getConsentState();
  if (!state) return false;
  return state.email_opt_in || state.whatsapp_opt_in;
}

/**
 * Get consent tags to apply (including ops:no_consent if needed)
 */
export function getConsentTags(): { add: string[]; remove: string[] } {
  const state = getConsentState();
  const add: string[] = [];
  const remove: string[] = [];

  if (state?.email_opt_in) add.push("consent:email_opt_in");
  if (state?.whatsapp_opt_in) add.push("consent:whatsapp_opt_in");
  if (state?.research_opt_in) add.push("consent:research_ok");

  if (!hasAnyConsent()) {
    add.push("ops:no_consent");
  } else {
    remove.push("ops:no_consent");
  }

  return { add, remove };
}

// ============= Engagement Score =============

const SCORE_POINTS: Record<string, number> = {
  quiz_completed: 10,
  routine_step_checked: 5,
  skin_log_created: 4,
  add_to_cart: 8,
  begin_checkout: 12,
  purchase: 20,
};

/**
 * Get current engagement score
 */
export function getEngagementScore(): EngagementScore {
  if (typeof window === "undefined") return { total: 0, last_event: "", updated_at: "" };

  try {
    const stored = localStorage.getItem(SCORE_KEY);
    if (stored) {
      return JSON.parse(stored) as EngagementScore;
    }
  } catch { /* ignore */ }

  return { total: 0, last_event: "", updated_at: "" };
}

/**
 * Add points to engagement score
 */
export function addScorePoints(eventName: string): EngagementScore {
  const points = SCORE_POINTS[eventName] || 0;
  if (points === 0) return getEngagementScore();

  const current = getEngagementScore();
  const updated: EngagementScore = {
    total: current.total + points,
    last_event: eventName,
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(SCORE_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }

  return updated;
}

/**
 * Calculate intent band from score
 */
export function getIntentBand(): "intent:low" | "intent:medium" | "intent:high" {
  const score = getEngagementScore();
  if (score.total >= 36) return "intent:high";
  if (score.total >= 16) return "intent:medium";
  return "intent:low";
}

/**
 * Get intent tags to apply (only current band, remove others)
 */
export function getIntentTags(): { add: string[]; remove: string[] } {
  const current = getIntentBand();
  const allIntentTags = ["intent:low", "intent:medium", "intent:high"];
  
  return {
    add: [current],
    remove: allIntentTags.filter(t => t !== current),
  };
}

// ============= Deduplication =============

function getDedupKey(eventName: string, extraKey?: string): string {
  return `${DEDUPE_PREFIX}${eventName}${extraKey ? `_${extraKey}` : ""}`;
}

export function shouldDedupe(eventName: string, extraKey?: string): boolean {
  if (typeof window === "undefined") return false;

  try {
    const key = getDedupKey(eventName, extraKey);
    const lastSent = localStorage.getItem(key);

    if (!lastSent) return false;

    const windowSeconds = DEDUPE_WINDOWS[eventName] || 60;
    const elapsed = (Date.now() - parseInt(lastSent, 10)) / 1000;

    return elapsed < windowSeconds;
  } catch {
    return false;
  }
}

function markEventSent(eventName: string, extraKey?: string): void {
  if (typeof window === "undefined") return;

  try {
    const key = getDedupKey(eventName, extraKey);
    localStorage.setItem(key, Date.now().toString());

    // Clean old dedupe entries (keep max 50)
    cleanupDedupeEntries();
  } catch { /* ignore */ }
}

function cleanupDedupeEntries(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DEDUPE_PREFIX)) {
        keys.push(key);
      }
    }

    if (keys.length > 50) {
      const entries = keys.map(k => ({
        key: k,
        time: parseInt(localStorage.getItem(k) || "0", 10),
      }));
      entries.sort((a, b) => a.time - b.time);

      const toRemove = entries.slice(0, entries.length - 50);
      toRemove.forEach(e => localStorage.removeItem(e.key));
    }
  } catch { /* ignore */ }
}

// ============= UTM & Auto Props =============

function getUTMProps(): Record<string, string> {
  if (typeof window === "undefined") return {};

  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    const result: Record<string, string> = {};
    for (const key of utmKeys) {
      const value = params.get(key);
      if (value) result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

function getFirstPage(): string {
  if (typeof window === "undefined") return "";

  try {
    let first = localStorage.getItem(FIRST_PAGE_KEY);
    if (!first) {
      first = window.location.href;
      localStorage.setItem(FIRST_PAGE_KEY, first);
    }
    return first;
  } catch {
    return typeof window !== "undefined" ? window.location.href : "";
  }
}

function getDevice(): "web" | "mobile" {
  if (typeof window === "undefined") return "web";

  try {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/.test(ua)) return "mobile";
    return "web";
  } catch {
    return "web";
  }
}

async function buildPayload(additionalProps?: Record<string, unknown>): Promise<Record<string, unknown>> {
  const userId = await getUserId();
  const utmParams = getUTMProps();

  return {
    anon_id: getAnonId(),
    session_id: getSessionId(),
    lead_id: getLeadId(),
    user_id: userId,
    external_id: getExternalId(),
    source_page: typeof window !== "undefined" ? window.location.pathname : "",
    page_url: typeof window !== "undefined" ? window.location.href : "",
    occurred_at: new Date().toISOString(),
    device: getDevice(),
    locale: "pt-BR",
    first_page: getFirstPage(),
    ...utmParams,
    ...additionalProps,
  };
}

// ============= CRM Endpoint =============

function getCRMEndpoint(): string {
  const envEndpoint = import.meta.env.VITE_CRM_EVENTS_ENDPOINT;
  if (envEndpoint) return envEndpoint;

  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (projectId) {
    return `https://${projectId}.supabase.co/functions/v1/crm-events`;
  }

  return "https://ajkvzhvrmnkfuraahtqe.supabase.co/functions/v1/crm-events";
}

// ============= Normalize Event Name =============

function toSnakeCase(str: string): string {
  return (str || "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[-\s]+/g, "_")
    .toLowerCase()
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function isEventAllowed(event: string): boolean {
  const normalized = toSnakeCase(event);
  
  if (CRM_ALLOWED_EVENTS.has(normalized)) return true;
  if (normalized.startsWith("journey:") || normalized.startsWith("need:")) return true;
  if (normalized.startsWith("consent_")) return true;
  
  return false;
}

// ============= Main Track Function =============

export async function track(event: string, payload?: TrackPayload): Promise<void> {
  const normalizedEvent = toSnakeCase(event);
  const debug = isCrmDebug();

  // Event governance
  if (!isEventAllowed(normalizedEvent)) {
    if (debug) {
      const isBlocked = CRM_BLOCKED_EVENTS.has(normalizedEvent);
      console.log(`[CRM] 🚫 BLOCKED: ${normalizedEvent}`, isBlocked ? "(explicit)" : "(not allowed)");
    }
    return;
  }

  // Build payload
  const autoProps = await buildPayload(payload?.properties);

  const body = {
    event: normalizedEvent,
    source: "site",
    email: payload?.email || null,
    phone: payload?.phone || null,
    name: payload?.name || null,
    properties: {
      ...autoProps,
      external_id: getExternalId(),
    },
  };

  // HARD BLOCK: no external_id
  if (!body.properties?.external_id) {
    console.error("[CRM] BLOCKED: external_id missing", body);
    return;
  }

  // Dedupe
  const dedupeKey = `${normalizedEvent}:${autoProps.source_page || ""}`;
  if (shouldDedupe(normalizedEvent, dedupeKey)) {
    if (debug) {
      console.log(`[CRM] ⏭️ Dedupe: ${normalizedEvent}`);
    }
    return;
  }
  markEventSent(normalizedEvent, dedupeKey);

  // Add score points (internal)
  if (SCORE_POINTS[normalizedEvent]) {
    addScorePoints(normalizedEvent);
  }

  if (debug) {
    console.log(`[CRM] 📤 ${normalizedEvent}`, body);
  }

  try {
    const endpoint = getCRMEndpoint();
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      // keepalive lets the request survive page navigation (e.g. begin_checkout
      // fired right before the browser navigates to the Shopify checkout).
      keepalive: true,
    });

    if (debug) {
      console.log(`[CRM] ${res.ok ? "✅" : "❌"} ${normalizedEvent}: ${res.status}`);
    }

    if (!res.ok) {
      const text = await res.text();
      console.warn(`[CRM] Failed "${normalizedEvent}": ${res.status}`, text);
    }
  } catch (err) {
    // Fail-soft: never let a CRM/network error break the checkout funnel.
    console.warn(`[CRM] Network error "${normalizedEvent}" (non-blocking):`, err);
  }
}

// ============= High-Level Event Functions =============

/**
 * Track site_visited (1x per session, 30min dedupe)
 */
export function trackSiteVisited(): void {
  void track("site_visited", {
    properties: {
      tags_add: ["journey:visited", "seg:b2c", resolveIdentityTag()],
    },
  });
}

/**
 * Track PDP viewed (1x per product per session)
 */
export function trackPdpViewed(sku: string, productName: string, catTag?: string): void {
  const tags = ["journey:pdp_viewer"];
  if (catTag) tags.push(catTag.startsWith("cat:") ? catTag : `cat:${catTag}`);

  void track("pdp_viewed", {
    properties: {
      sku,
      product_name: productName,
      cat_tag: catTag,
      tags_add: tags,
      dedupe_key: sku,
    },
  });
}

/**
 * Track quiz started
 */
export function trackQuizStarted(): void {
  void track("quiz_started", {
    properties: {
      tags_add: ["journey:quiz_started"],
    },
  });
}

/**
 * Track quiz completed with need tags
 */
export function trackQuizCompleted(options: {
  skinTypeLabel: string;
  primaryGoal: string;
  routineSteps: string[];
  needTags: string[];
}): void {
  const { skinTypeLabel, primaryGoal, routineSteps, needTags } = options;

  const tags = [
    "journey:quiz_completed",
    "journey:engaged",
    ...needTags.map(t => t.startsWith("need:") ? t : `need:${t}`),
  ];

  void track("quiz_completed", {
    properties: {
      skin_type_label: skinTypeLabel,
      primary_goal: primaryGoal,
      routine_steps: routineSteps,
      need_tags: needTags,
      tags_add: tags,
    },
  });
}

/**
 * Track add to cart
 */
export function trackAddToCart(
  sku: string,
  qty: number,
  price: number,
  name: string,
  catTag?: string,
): void {
  const tags = ["journey:add_to_cart"];
  if (catTag) tags.push(catTag.startsWith("cat:") ? catTag : `cat:${catTag}`);

  void track("add_to_cart", {
    properties: {
      sku,
      qty,
      price,
      product_name: name,
      value: qty * price,
      tags_add: tags,
      dedupe_key: sku,
    },
  });
}

/**
 * Track begin checkout
 */
export function trackBeginCheckout(properties?: Record<string, unknown>): void {
  void track("begin_checkout", {
    properties: {
      ...properties,
      tags_add: ["journey:begin_checkout"],
    },
  });
}

/**
 * Track purchase
 */
export function trackPurchase(options: {
  orderId: string;
  value: number;
  isFirstPurchase?: boolean;
}): void {
  const { orderId, value, isFirstPurchase } = options;

  const tags = ["journey:purchased"];
  if (isFirstPurchase) {
    tags.push("journey:first_purchase");
  } else {
    tags.push("journey:repeat_purchase");
  }

  void track("purchase", {
    properties: {
      order_id: orderId,
      value,
      is_first_purchase: isFirstPurchase,
      tags_add: tags,
      dedupe_key: orderId,
    },
  });
}

/**
 * Track routine step checked (1x/day max)
 */
export function trackRoutineStepChecked(totalChecksToday: number, routineId?: string): void {
  void track("routine_step_checked", {
    properties: {
      total_checks_today: totalChecksToday,
      routine_id: routineId,
      tags_add: ["journey:engaged"],
    },
  });
}

/**
 * Track skin log created (1x/day max)
 */
export function trackSkinLogCreated(options: {
  emoji: string;
  mainNeedTag: string;
  hasNote: boolean;
}): void {
  const { emoji, mainNeedTag, hasNote } = options;
  const needTag = mainNeedTag.startsWith("need:") ? mainNeedTag : `need:${mainNeedTag}`;

  void track("skin_log_created", {
    properties: {
      emoji,
      main_need_tag: mainNeedTag,
      has_note: hasNote,
      tags_add: [needTag, "journey:engaged"],
    },
  });
}

/**
 * Track daily recommendation shown (1x/day max)
 */
export function trackDailyRecommendationShown(options?: {
  humidityBucket?: "low" | "medium" | "high";
  mainNeedTag?: string;
  routineType?: string;
}): void {
  void track("daily_recommendation_shown", {
    properties: {
      humidity_bucket: options?.humidityBucket,
      main_need_tag: options?.mainNeedTag,
      routine_type: options?.routineType,
      // No tags to avoid noise
    },
  });
}

/**
 * Track PDF export
 */
export function trackRoutineExportedPdf(routineId?: string, hasDeepeningData = false): void {
  void track("routine_exported_pdf", {
    properties: {
      routine_id: routineId,
      has_deepening_data: hasDeepeningData,
      tags_add: ["journey:engaged"],
    },
  });
}

/**
 * Track routine shared via WhatsApp
 */
export function trackRoutineShared(routineId?: string, channel: "whatsapp" = "whatsapp"): void {
  void track("routine_shared", {
    properties: {
      routine_id: routineId,
      channel,
      tags_add: ["journey:engaged"],
    },
  });
}

/**
 * Track routine link copied
 */
export function trackRoutineLinkCopied(routineId?: string): void {
  void track("routine_link_copied", {
    properties: {
      routine_id: routineId,
      tags_add: ["journey:engaged"],
    },
  });
}

/**
 * Track consent research opt-in
 */
export function trackConsentResearchOptIn(): void {
  setConsentState({ research_opt_in: true });
  void track("consent_research_opt_in", {
    properties: {
      tags_add: ["consent:research_ok"],
    },
  });
}

/**
 * Track lead capture with identity and consent
 */
export function trackLeadCapture(
  source: "popup" | "footer" | "quiz" | "checkout" | "blog" | "affiliate" | "b2b_page" | "clinicas" | "contato_profissional" | "profissional_registro" | "revendedor",
  data: { email?: string; phone?: string; name?: string },
  consent?: { emailOptIn?: boolean; whatsappOptIn?: boolean },
  segment: "seg:b2c" | "seg:b2b_clinica" | "seg:b2b_profissional_saude" | "seg:b2b_revendedor" = "seg:b2c",
): void {
  // Store identity
  if (data.email) setStoredEmail(data.email);
  if (data.phone) setStoredPhone(data.phone);

  // Store consent
  if (consent) {
    // Only update the channels explicitly provided; an omitted channel keeps
    // its stored value (signing up for e-mail must not revoke WhatsApp).
    const update: { email_opt_in?: boolean; whatsapp_opt_in?: boolean } = {};
    if (consent.emailOptIn !== undefined) update.email_opt_in = consent.emailOptIn;
    if (consent.whatsappOptIn !== undefined) update.whatsapp_opt_in = consent.whatsappOptIn;
    setConsentState(update);
  }

  // Build tags
  const identityTag = resolveIdentityTag();
  const tags = ["journey:lead_captured", segment, identityTag];
  const removeTags = getIdentityTagsToRemove(identityTag);

  if (consent?.emailOptIn) tags.push("consent:email_opt_in");
  if (consent?.whatsappOptIn) tags.push("consent:whatsapp_opt_in");

  if (!hasAnyConsent()) {
    tags.push("ops:no_consent");
  } else {
    removeTags.push("ops:no_consent");
  }

  // Determine captured type
  const hasEmail = Boolean(data.email);
  const hasPhone = Boolean(data.phone);
  let capturedType: "email" | "phone" | "both" = "email";
  if (hasEmail && hasPhone) capturedType = "both";
  else if (hasPhone) capturedType = "phone";

  void track("lead_capture", {
    email: data.email,
    phone: data.phone,
    name: data.name,
    properties: {
      capture_source: source,
      captured_type: capturedType,
      segment,
      tags_add: tags,
      tags_remove: removeTags,
    },
  });
}

/**
 * Track WhatsApp click
 */
export function trackWhatsAppClick(location: string, phoneTarget?: string): void {
  void track("whatsapp_click", {
    properties: {
      location,
      phone_target: phoneTarget,
    },
  });
}

/**
 * Generic event (for edge cases)
 */
export function trackGenericEvent(event: string, properties?: Record<string, unknown>): void {
  void track(event, { properties });
}

// ============= Debug Helpers =============

type DebugEventData = {
  event: string;
  status: "pending" | "success" | "error";
  httpStatus?: number;
  endpoint: string;
  body: Record<string, unknown>;
};

type DebugEventListener = (data: DebugEventData) => void;

const debugListeners: Set<DebugEventListener> = new Set();

export function subscribeToDebugEvents(listener: DebugEventListener): () => void {
  debugListeners.add(listener);
  return () => debugListeners.delete(listener);
}

// ============= Session Initialization =============

let sessionInitialized = false;

/**
 * Initialize session tracking (call once on app load)
 */
export function initSessionTracking(): void {
  if (typeof window === "undefined") return;
  if (sessionInitialized) return;

  sessionInitialized = true;
  trackSiteVisited();
}

// ============= Deprecated Functions (backwards compatibility) =============

/** @deprecated Use trackPdpViewed instead */
export function trackViewPDP(sku: string, properties?: Record<string, unknown>): void {
  if (isCrmDebug()) {
    console.log("[CRM] trackViewPDP is deprecated, use trackPdpViewed");
  }
}

/** @deprecated page_view is blocked by governance */
export function trackPageView(properties?: Record<string, unknown>): void {
  // No-op - blocked by governance
}
